import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCcw, Play, Archive, Edit3, Copy, ShieldCheck } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import PlatformLayout from "@/components/PlatformLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CfgStatusBadge, CfgEnvBadge, ToggleDot } from "./CfgBadges";
import ConfigurationFormDialog from "./ConfigurationFormDialog";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("id-ID"); } catch { return iso; }
}

function Row({ label, value, testid, mono = false }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5 border-b border-white/5 last:border-0">
      <div className="text-[11px] text-white/50 uppercase tracking-wider shrink-0">{label}</div>
      <div className={`text-sm text-white text-right truncate min-w-0 ${mono ? "font-mono" : ""}`} data-testid={testid}>{value ?? "—"}</div>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-4" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-widest text-white/40 font-bold mb-2">{title}</div>
      {children}
    </div>
  );
}

function SecretRow({ label, value, testid }) {
  if (!value) return <Row label={label} value="—" testid={testid} />;
  return (
    <div className="flex items-center justify-between gap-2 py-1.5 border-b border-white/5 last:border-0">
      <div className="text-[11px] text-white/50 uppercase tracking-wider shrink-0">{label}</div>
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 text-[10px] font-black font-mono" data-testid={testid}>
        <ShieldCheck size={10} /> {value.slice(0, 22)}…
      </span>
    </div>
  );
}

export default function ConfigurationDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [effective, setEffective] = useState(null);
  const [envLines, setEnvLines] = useState("");
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, ts] = await Promise.all([
        api.get(`/platform/configurations/${id}/sanitized`),
        api.get("/platform/tenants"),
      ]);
      setCfg(c.data);
      setTenants(ts.data.items || []);
      try {
        const eff = await api.get(`/platform/configurations/tenant/${c.data.tenant_id}/effective?environment=${c.data.environment}`);
        setEffective(eff.data.effective);
        setEnvLines(eff.data.env_lines || "");
      } catch { /* effective is optional */ }
    } catch (e) { notifyError(e); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const act = async (verb) => {
    setBusy(true);
    try {
      await api.post(`/platform/configurations/${id}/${verb}`, {});
      toast.success(`Config ${verb} berhasil.`);
      load();
    } catch (e) { notifyError(e, `Gagal ${verb}`); }
    finally { setBusy(false); }
  };

  const copyEnv = async () => {
    if (!envLines) return;
    try {
      await navigator.clipboard.writeText(envLines);
      toast.success("Effective ENV disalin ke clipboard.");
    } catch { toast.error("Gagal salin."); }
  };

  if (loading || !cfg) {
    return (
      <PlatformLayout title="Configuration Detail">
        <div className="text-white/40 text-sm p-6 rounded-2xl border border-white/10 text-center">Memuat…</div>
      </PlatformLayout>
    );
  }

  const tenant = tenants.find((t) => t.tenant_id === cfg.tenant_id);
  const canActivate = cfg.status === "DRAFT";
  const canEdit = cfg.status !== "ARCHIVED";
  const canArchive = cfg.status !== "ARCHIVED";

  return (
    <PlatformLayout title={`Configuration · v${cfg.config_version}`}>
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <Button variant="ghost" onClick={() => nav("/platform/configurations")} className="text-white/60 hover:text-white hover:bg-white/10" data-testid="cfg-back-btn">
          <ArrowLeft size={14} className="mr-1" /> Kembali
        </Button>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <Button variant="ghost" onClick={load} className="text-white/60 hover:text-white hover:bg-white/10" data-testid="cfg-refresh-btn">
            <RefreshCcw size={14} className="mr-1" /> Refresh
          </Button>
          {canEdit && (
            <Button onClick={() => setEditOpen(true)} className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="cfg-detail-edit">
              <Edit3 size={14} className="mr-1" /> Edit
            </Button>
          )}
          {canActivate && (
            <Button onClick={() => act("activate")} disabled={busy} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="cfg-detail-activate">
              <Play size={14} className="mr-1" /> Activate
            </Button>
          )}
          {canArchive && (
            <Button onClick={() => act("archive")} disabled={busy} variant="outline" className="border-neutral-500/40 text-neutral-300 hover:bg-neutral-500/10" data-testid="cfg-detail-archive">
              <Archive size={14} className="mr-1" /> Archive
            </Button>
          )}
        </div>
      </div>

      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <div className="text-white font-display font-black text-lg" data-testid="cfg-detail-tenant">{tenant?.tenant_name || cfg.tenant_id.slice(0, 8)}</div>
        <CfgStatusBadge status={cfg.status} />
        <CfgEnvBadge environment={cfg.environment} />
        <span className="text-xs text-white/50 font-mono">v{cfg.config_version}</span>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Section title="General" testid="cfg-section-general">
          <Row label="App Name" value={cfg.general?.app_name} testid="cfg-info-app-name" />
          <Row label="Business" value={cfg.general?.business_name} testid="cfg-info-biz" />
          <Row label="Support Email" value={cfg.general?.support_email} testid="cfg-info-support-email" />
          <Row label="Support Phone" value={cfg.general?.support_phone} testid="cfg-info-support-phone" />
          <Row label="Address" value={cfg.general?.business_address} testid="cfg-info-address" />
        </Section>
        <Section title="Localization" testid="cfg-section-localization">
          <Row label="Timezone" value={cfg.localization?.timezone} testid="cfg-info-tz" mono />
          <Row label="Language" value={cfg.localization?.language} testid="cfg-info-lang" mono />
          <Row label="Date Format" value={cfg.localization?.date_format} testid="cfg-info-date" mono />
          <Row label="Time Format" value={cfg.localization?.time_format} testid="cfg-info-time" mono />
        </Section>
        <Section title="Currency & Tax" testid="cfg-section-currency">
          <Row label="Currency" value={`${cfg.currency?.currency_symbol || ""} ${cfg.currency?.currency_code || ""}`} testid="cfg-info-currency" />
          <Row label="Decimals" value={cfg.currency?.decimal_digits} testid="cfg-info-decimals" mono />
          <Row label="Tax Enabled" value={<ToggleDot on={!!cfg.tax?.tax_enabled} />} testid="cfg-info-tax-enabled" />
          <Row label={`${cfg.tax?.tax_name || "Tax"} Rate`} value={`${cfg.tax?.tax_rate || 0}%`} testid="cfg-info-tax-rate" />
          <Row label="Included" value={<ToggleDot on={!!cfg.tax?.tax_included} />} testid="cfg-info-tax-included" />
        </Section>
        <Section title="Invoice" testid="cfg-section-invoice">
          <Row label="Prefix" value={cfg.invoice?.invoice_prefix} testid="cfg-info-inv-prefix" mono />
          <Row label="Format" value={cfg.invoice?.invoice_number_format} testid="cfg-info-inv-format" mono />
          <Row label="Show Tax" value={<ToggleDot on={!!cfg.invoice?.show_tax} />} />
          <Row label="Show Logo" value={<ToggleDot on={!!cfg.invoice?.show_logo} />} />
        </Section>
        <Section title="Email" testid="cfg-section-email">
          <Row label="Provider" value={cfg.email?.provider} />
          <Row label="Host" value={cfg.email?.smtp_host} mono />
          <Row label="Port" value={cfg.email?.smtp_port} mono />
          <Row label="Sender" value={`${cfg.email?.sender_name || ""} <${cfg.email?.sender_email || ""}>`} />
          <SecretRow label="Username Ref" value={cfg.email?.smtp_username_ref} testid="cfg-info-email-username-ref" />
          <SecretRow label="Password Ref" value={cfg.email?.smtp_password_ref} testid="cfg-info-email-password-ref" />
          <Row label="Enabled" value={<ToggleDot on={!!cfg.email?.enabled} />} testid="cfg-info-email-enabled" />
        </Section>
        <Section title="WhatsApp" testid="cfg-section-whatsapp">
          <Row label="Provider" value={cfg.whatsapp?.provider} />
          <Row label="Phone" value={cfg.whatsapp?.phone_number} mono />
          <SecretRow label="API Key Ref" value={cfg.whatsapp?.api_key_ref} testid="cfg-info-wa-key-ref" />
          <Row label="Enabled" value={<ToggleDot on={!!cfg.whatsapp?.enabled} />} />
        </Section>
        <Section title="Payment" testid="cfg-section-payment">
          <Row label="Provider" value={cfg.payment?.provider} />
          <Row label="Mode" value={cfg.payment?.mode} />
          <SecretRow label="Merchant ID Ref" value={cfg.payment?.merchant_id_ref} testid="cfg-info-pay-mid-ref" />
          <SecretRow label="Server Key Ref" value={cfg.payment?.server_key_ref} testid="cfg-info-pay-server-ref" />
          <SecretRow label="Client Key Ref" value={cfg.payment?.client_key_ref} testid="cfg-info-pay-client-ref" />
          <Row label="Enabled" value={<ToggleDot on={!!cfg.payment?.enabled} />} testid="cfg-info-pay-enabled" />
        </Section>
        <Section title="Shipping" testid="cfg-section-shipping">
          <Row label="Provider" value={cfg.shipping?.provider} />
          <Row label="City" value={cfg.shipping?.origin_city} />
          <Row label="Postal" value={cfg.shipping?.origin_postal_code} mono />
          <Row label="Enabled" value={<ToggleDot on={!!cfg.shipping?.enabled} />} testid="cfg-info-ship-enabled" />
        </Section>
        <Section title="Storage" testid="cfg-section-storage">
          <Row label="Driver" value={cfg.storage?.driver} mono />
          <Row label="Base Path" value={cfg.storage?.base_path} mono />
          <Row label="Bucket" value={cfg.storage?.bucket_name} />
          <Row label="Region" value={cfg.storage?.region} mono />
          <SecretRow label="Access Key Ref" value={cfg.storage?.access_key_ref} testid="cfg-info-store-ak-ref" />
          <SecretRow label="Secret Key Ref" value={cfg.storage?.secret_key_ref} testid="cfg-info-store-sk-ref" />
        </Section>
      </div>

      {/* Effective Config ENV Preview */}
      <div className="mt-5 rounded-2xl border border-white/10 bg-black/60 overflow-hidden" data-testid="cfg-effective-block">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 bg-white/5">
          <ShieldCheck size={14} className="text-emerald-300" />
          <div className="text-[11px] uppercase tracking-widest text-white/70 font-black">Effective Config Preview (sanitized)</div>
          <Button size="sm" variant="ghost" onClick={copyEnv} className="ml-auto text-white/60 hover:text-white hover:bg-white/10" data-testid="cfg-copy-env">
            <Copy size={12} className="mr-1" /> Copy
          </Button>
        </div>
        <pre className="p-4 text-xs font-mono text-emerald-200 overflow-auto max-h-96 whitespace-pre-wrap break-words" data-testid="cfg-effective-content">
{envLines || "Belum ada effective config aktif untuk environment ini."}
        </pre>
        <div className="px-4 py-2 border-t border-white/10 bg-white/5 text-[10px] text-white/40">
          Secrets tampil sebagai reference (secret_xxx). Raw JWT/SMTP/PAYMENT/STORAGE keys tidak pernah dikirim ke frontend.
        </div>
      </div>

      <div className="mt-4">
        <Link to="/platform/configurations" className="text-xs text-white/50 hover:text-white underline">← Semua configuration</Link>
      </div>

      <ConfigurationFormDialog tenants={tenants} existing={cfg} open={editOpen} onClose={() => setEditOpen(false)} onSaved={load} />
    </PlatformLayout>
  );
}
